import { Controller } from '@hotwired/stimulus'
import SFetch from '@app/utils/sfetch'
import { SfLog } from '@app/adapters/logger'

type CheckStatus =
  | 'found'
  | 'connected'
  | 'fallback'
  | 'denied'
  | 'not_found'
  | 'error'

interface CheckResult {
  status: CheckStatus
  message: string
}

const SUCCESS_STATUSES: CheckStatus[] = ['found', 'connected']

export default class extends Controller<HTMLFormElement> {
  static targets = [
    'onPremise',
    'onPremiseFields',
    'databaseUrl',
    'databaseName',
    'message',
    'messageText',
    'checkButton',
    'submitButton',
  ]
  static values = { checkUrl: String, errorMessage: String }

  declare readonly onPremiseTarget: HTMLInputElement
  declare readonly onPremiseFieldsTarget: HTMLElement
  declare readonly databaseUrlTarget: HTMLInputElement
  declare readonly databaseNameTarget: HTMLInputElement
  declare readonly messageTarget: HTMLElement
  declare readonly messageTextTarget: HTMLElement
  declare readonly checkButtonTarget: HTMLButtonElement
  declare readonly submitButtonTarget: HTMLButtonElement
  declare readonly checkUrlValue: string
  declare readonly errorMessageValue: string

  private generation = 0

  toggleOnPremise(): void {
    const onPremise = this.onPremiseTarget.checked
    this.onPremiseFieldsTarget.classList.toggle('sf-hidden', !onPremise)
    if (!onPremise) {
      this.databaseUrlTarget.value = ''
      this.databaseNameTarget.value = ''
    }
    this.invalidate()
  }

  invalidate(): void {
    this.generation += 1
    this.submitButtonTarget.disabled = true
    this.messageTarget.classList.add('sf-hidden')
  }

  check(event: Event): void {
    event.preventDefault()
    if (this.checkButtonTarget.getAttribute('aria-busy') === 'true') return

    this.setBusy(true)
    const generation = this.generation
    void (async (): Promise<void> => {
      try {
        const result = await this.requestCheck()
        if (generation === this.generation) this.show(result)
      } catch (error) {
        SfLog.error('Odoo setup check failed', { error })
        if (generation === this.generation) {
          this.show({ status: 'error', message: this.errorMessageValue })
        }
      } finally {
        this.setBusy(false)
      }
    })()
  }

  private async requestCheck(): Promise<CheckResult> {
    const response = await SFetch.raw(this.checkUrlValue, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(this.element),
    })
    return (await response.json()) as CheckResult
  }

  private show({ status, message }: CheckResult): void {
    if (status === 'fallback') {
      this.onPremiseTarget.checked = true
      this.onPremiseFieldsTarget.classList.remove('sf-hidden')
    }

    const success = SUCCESS_STATUSES.includes(status)
    this.submitButtonTarget.disabled = !success
    this.messageTextTarget.textContent = message
    this.messageTarget.classList.remove(
      'sf-hidden',
      'sf-banner-inline-neutral',
      'sf-banner-inline-success',
      'sf-banner-inline-warning',
      'sf-banner-inline-danger'
    )
    this.messageTarget.classList.add(this.bannerClass(status))
  }

  private bannerClass(status: CheckStatus): string {
    if (SUCCESS_STATUSES.includes(status)) return 'sf-banner-inline-success'
    return status === 'fallback'
      ? 'sf-banner-inline-warning'
      : 'sf-banner-inline-danger'
  }

  private setBusy(busy: boolean): void {
    if (busy) {
      this.checkButtonTarget.setAttribute('aria-busy', 'true')
      this.checkButtonTarget.setAttribute('aria-disabled', 'true')
    } else {
      this.checkButtonTarget.removeAttribute('aria-busy')
      this.checkButtonTarget.removeAttribute('aria-disabled')
    }
  }
}
